import { useToast } from '@/hooks'
import { useAllTransactions, useProjectMutations } from '@/modules/projects'
import type { EditRequestDTO } from '@/modules/projects/stores/types'
import { assignPages, createStatementFrom, statementViews } from './assignment'
import {
  isUnfiledPile,
  pageNumbersByFile,
  planRecuts,
  touchedFiles,
  unfiledPileId,
} from './fromProject'
import type { ArrangementLookup } from './lookup'
import {
  type NewStatementPlace,
  relabelIntoNewStatement,
  relabelMove,
  takeOutRequests,
} from './moves'
import type {
  Arrangement,
  DocumentPage,
  SourceDocument,
  StatementView,
} from './types'
import type { ConfirmedEdits } from './useConfirmedEdits'

interface PageMovesInput {
  sourceDocument: SourceDocument
  arrangement: Arrangement
  lookup: ArrangementLookup
  selectedPages: number[]
  pickedUp: DocumentPage[]
  edits: ConfirmedEdits
}

// Every move of the picked-up pages, and of whole statements, asked as one confirmed edit.
export const usePageMoves = ({
  sourceDocument,
  arrangement,
  lookup,
  selectedPages,
  pickedUp,
  edits,
}: PageMovesInput) => {
  const transactions = useAllTransactions()
  const { resegmentStatement } = useProjectMutations()
  const { errorToast } = useToast()
  const { holderByPage, pageAt, pagesOf, nameOf, viewOf } = lookup

  const sendRecut = async (request: EditRequestDTO) => {
    if (request.kind === 'recut_file')
      await resegmentStatement(request.statement_id, request.segments)
  }

  const nothingToChange = () =>
    errorToast(
      'Nothing to change',
      'These pages are already where they would go.',
    )

  // What cannot be a relabel is a re-cut: lukita reads the statements it changes again. It is asked
  // and sent in one step like every other edit, so nothing waits on screen for a second button.
  const reCut = (next: Arrangement) => {
    const plan = planRecuts(
      statementViews(next),
      touchedFiles(sourceDocument, arrangement, next),
      pageAt,
      nameOf,
    )
    if (plan.blocked.length > 0) {
      errorToast(
        'This file cannot be re-cut yet',
        `${plan.blocked.join(', ')} would hold pages that do not sit together in one file. If a page was taken out of its middle, undo that in the edit history first.`,
      )
      return
    }
    if (plan.recuts.length === 0) {
      nothingToChange()
      return
    }
    edits.propose(
      plan.recuts.map((recut) => ({
        kind: 'recut_file' as const,
        statement_id: recut.fileId,
        segments: recut.ranges,
      })),
      sendRecut,
    )
  }

  // The user's word is the account: filed pages are relabelled and nothing is read again. Only
  // pages with no rows yet go through a re-cut.
  const handOver = (
    requests: EditRequestDTO[] | null,
    next: () => Arrangement,
  ) => {
    if (!requests) {
      reCut(next())
      return
    }
    if (requests.length === 0) {
      nothingToChange()
      return
    }
    edits.propose(requests)
  }

  // Pages that belong to no statement wait in their own file's pile, which is where the re-cut
  // leaves them out from.
  const unfile = (from: Arrangement, pageNumbers: number[]) => {
    let next = from
    for (const [fileId, pages] of pageNumbersByFile(pagesOf(pageNumbers))) {
      const pileId = unfiledPileId(fileId)
      next = next.statements.some((held) => held.id === pileId)
        ? assignPages(next, pages, pileId)
        : createStatementFrom(next, pages, null, null, pileId)
    }
    return next
  }

  const takeOut = () =>
    handOver(takeOutRequests(holderByPage, pickedUp), () =>
      unfile(arrangement, selectedPages),
    )

  // A pile of left-out pages is not a statement: dropping pages on it takes them out.
  const putIn = (held: StatementView, after: DocumentPage | null) =>
    isUnfiledPile(held.statement.id)
      ? takeOut()
      : handOver(relabelMove(holderByPage, pickedUp, held, after), () =>
          assignPages(arrangement, selectedPages, held.statement.id),
        )

  const lastPageOf = (held: StatementView) => {
    const last = held.pages[held.pages.length - 1]
    return last === undefined ? null : pageAt(last)
  }

  // Pages dropped on a band are filed under that band's statement, same question as a drop on a
  // month. Pages already filed there have nothing to answer.
  const fileUnderStatement = (statementId: string, after: number | null) => {
    const found = viewOf(statementId)
    const alreadyThere = selectedPages.every(
      (page) => holderByPage.get(page)?.statement.id === statementId,
    )
    if (!found || alreadyThere) return
    putIn(found, after === null ? null : pageAt(after))
  }

  // lukita files a row by its upload and its sheet in that upload, the same key a page carries.
  const rowDatesOn = (page: DocumentPage) =>
    transactions
      .filter(
        (row) =>
          !row.discardedAt &&
          row.statementId === page.source.fileId &&
          row.sourcePage === page.source.pageInFile,
      )
      .map((row) => row.date)

  const putInNew = (place: NewStatementPlace) =>
    handOver(
      relabelIntoNewStatement(
        holderByPage,
        pickedUp,
        place,
        arrangement.accounts,
        rowDatesOn,
      ),
      () =>
        createStatementFrom(
          arrangement,
          selectedPages,
          place.period,
          place.accountId,
        ),
    )

  return {
    fileUnderStatement,
    putInNew,
    takeOut,
    putAtEnd: (held: StatementView) => putIn(held, lastPageOf(held)),
    // A statement made from the picked pages is filed under the account they sit in now.
    makeNewStatement: () =>
      putInNew({
        accountId:
          holderByPage.get(selectedPages[0] ?? 0)?.statement.accountId ?? null,
        period: null,
      }),
    // The statement goes, its pages stay: they wait in their file's pile like any page taken out.
    deleteStatement: (held: StatementView) =>
      reCut(unfile(arrangement, held.pages)),
  }
}
